import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useApi } from "../../api.ts";
import { useLive, useLiveEvent } from "../../live.tsx";
import { fmtMoney, fmtRelative, shortHash } from "../../format.ts";
import {
  Badge, Button, Card, EmptyState, Icon, Loading, LoadError, Mono, PageHeader, Stat, Table, TBody, Td, Th, THead, Tr, cn,
  type IconName,
} from "../../ui/index.tsx";
import { useGames, useTick } from "./_common.tsx";
import type { Alert, Metrics, Overview as OverviewData } from "../../../shared/types.ts";

const RANGES = [7, 30, 90] as const;

const ALERT_META: Record<Alert["type"], { label: string; tone: "warn" | "info" | "bad"; icon: IconName; text: string }> = {
  multi_hwid: { label: "Multiple HWIDs", tone: "bad", icon: "monitor", text: "text-bad" },
  multi_country: { label: "Multiple countries", tone: "warn", icon: "globe", text: "text-warn" },
  expiring: { label: "Expiring", tone: "info", icon: "clock", text: "text-info" },
};

export default function Overview() {
  const [days, setDays] = useState<number>(30);
  const overview = useApi<OverviewData>("/admin/overview");
  const metrics = useApi<Metrics>(`/admin/metrics?days=${days}`);
  const { connected, online } = useLive();
  const { nameOf } = useGames();
  useTick();

  useLiveEvent(["ticket"], () => { void overview.reload(); });
  useEffect(() => {
    const id = setInterval(() => { void overview.reload(); }, 60_000);
    return () => clearInterval(id);
  }, [overview.reload]);

  const stats = overview.data?.stats;
  const onlineUsers = useMemo(() => new Set(online.map((o) => o.user_id)).size, [online]);

  return (
    <>
      <PageHeader title="Overview" description="Licensing, activity and health at a glance"
        actions={<Button size="sm" icon="refresh" onClick={() => { void overview.reload(); void metrics.reload(); }}>Refresh</Button>} />

      {overview.error && !stats ? <LoadError error={overview.error} retry={overview.reload} /> : !stats ? <Loading /> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
            <Stat label="Users" value={stats.total_users.toLocaleString()} icon="users" />
            <Stat label="Licensed users" value={stats.active_users.toLocaleString()} hint="Active entitlement" icon="key" />
            <Stat label="Online now" value={connected ? onlineUsers : stats.online_now} hint={connected ? "Live" : "As of last refresh"} icon="activity" tone="ok" />
            <Stat label="Open tickets" value={stats.open_tickets} icon="ticket" tone={stats.open_tickets ? "warn" : undefined} />
            <Stat label="Revenue this month" value={fmtMoney(stats.revenue_month_cents, "GBP")} icon="card" />
            <Stat label="Revenue total" value={fmtMoney(stats.revenue_total_cents, "GBP")} icon="card" />
            <Stat label="Logins (24h)" value={stats.logins_24h.toLocaleString()} hint={`${stats.active_sessions} active sessions`} icon="logout" />
            <Stat label="Errors (24h)" value={stats.errors_24h.toLocaleString()} icon="alert" tone={stats.errors_24h ? "warn" : undefined} />
            <Stat label="Crashes (24h)" value={stats.crashes_24h.toLocaleString()} icon="bug" tone={stats.crashes_24h ? "bad" : undefined} />
            <Stat label="Unused keys" value={stats.unused_keys.toLocaleString()} icon="gift" />
            <Stat label="Expiring in 7 days" value={stats.expiring_7d.toLocaleString()} icon="clock" tone={stats.expiring_7d ? "warn" : undefined} />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card title="Alerts" description="Accounts worth a look" padded={false}>
              {!overview.data?.alerts.length ? (
                <EmptyState icon="shield" title="No alerts" body="Nothing suspicious right now." />
              ) : (
                <ul className="max-h-80 divide-y divide-line overflow-y-auto">
                  {overview.data.alerts.map((a, i) => {
                    const meta = ALERT_META[a.type];
                    return (
                      <li key={`${a.type}-${a.user_id}-${i}`}>
                        <Link to={`/admin/users/${a.user_id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-hover/60">
                          <Icon name={meta.icon} className={cn("size-4 shrink-0", meta.text)} />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium">{a.username}</div>
                            <div className="truncate text-xs text-muted">{a.detail}</div>
                          </div>
                          <Badge tone={meta.tone}>{meta.label}</Badge>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>

            <Card title="Online now" description={connected ? `${online.length} connections, live` : "Live feed disconnected"} padded={false}>
              {!online.length ? (
                <EmptyState icon="activity" title="Nobody online" />
              ) : (
                <div className="max-h-80 overflow-y-auto">
                  <Table>
                    <THead><tr><Th>User</Th><Th>Kind</Th><Th>Game</Th><Th>HWID</Th><Th>Connected</Th></tr></THead>
                    <TBody>
                      {online.map((o) => (
                        <Tr key={o.client_id}>
                          <Td><Link to={`/admin/users/${o.user_id}`} className="font-medium hover:text-accent">{o.username}</Link></Td>
                          <Td><Badge tone={o.kind === "loader" ? "accent" : "neutral"}>{o.kind}</Badge></Td>
                          <Td className="text-muted">{o.game ? nameOf(o.game) : "—"}</Td>
                          <Td><Mono>{shortHash(o.hwid, 8)}</Mono></Td>
                          <Td className="whitespace-nowrap text-muted">{fmtRelative(o.connected_at)}</Td>
                        </Tr>
                      ))}
                    </TBody>
                  </Table>
                </div>
              )}
            </Card>
          </div>

          <Card title="Trends"
            actions={
              <div className="flex gap-1">
                {RANGES.map((r) => (
                  <Button key={r} size="sm" variant={days === r ? "primary" : "ghost"} onClick={() => setDays(r)}>{r}d</Button>
                ))}
              </div>
            }>
            {metrics.error && !metrics.data ? <LoadError error={metrics.error} retry={metrics.reload} /> : !metrics.data ? <Loading /> : (
              <Charts metrics={metrics.data} />
            )}
          </Card>
        </div>
      )}
    </>
  );
}

function lastDays(n: number): string[] {
  const now = Date.now();
  return Array.from({ length: n }, (_, i) => new Date(now - (n - 1 - i) * 86_400_000).toISOString().slice(0, 10));
}

function Charts({ metrics }: { metrics: Metrics }) {
  const range = useMemo(() => lastDays(metrics.days), [metrics.days]);
  const logins = useMemo(() => {
    const by = new Map(metrics.logins_by_day.map((r) => [r.day, r]));
    return range.map((day) => ({ day, values: [by.get(day)?.success ?? 0, by.get(day)?.failed ?? 0] }));
  }, [metrics, range]);
  const growth = useMemo(() => {
    const by = new Map(metrics.user_growth.map((r) => [r.day, r.count]));
    return range.map((day) => ({ day, values: [by.get(day) ?? 0] }));
  }, [metrics, range]);

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div>
        <h3 className="mb-3 text-xs font-medium text-muted">Logins per day</h3>
        <BarChart data={logins} series={[{ name: "Successful", color: "bg-ok" }, { name: "Failed", color: "bg-bad" }]} />
      </div>
      <div>
        <h3 className="mb-3 text-xs font-medium text-muted">New users per day</h3>
        <BarChart data={growth} series={[{ name: "New users", color: "bg-accent" }]} />
      </div>
    </div>
  );
}

interface BarDatum { day: string; values: number[] }

function niceMax(n: number): number {
  if (n <= 4) return Math.max(n, 1);
  const pow = 10 ** Math.floor(Math.log10(n));
  return Math.ceil(n / pow) * pow;
}

function BarChart({ data, series }: { data: BarDatum[]; series: { name: string; color: string }[] }) {
  const max = niceMax(Math.max(0, ...data.map((d) => d.values.reduce((a, b) => a + b, 0))));
  const total = data.reduce((sum, d) => sum + d.values.reduce((a, b) => a + b, 0), 0);
  const shortDay = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC" });
  const first = data[0]?.day, mid = data[Math.floor(data.length / 2)]?.day, last = data[data.length - 1]?.day;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-4 text-xs text-muted">
        {series.map((s) => (
          <span key={s.name} className="inline-flex items-center gap-1.5"><span className={cn("size-2 rounded-sm", s.color)} />{s.name}</span>
        ))}
        <span className="ml-auto tabular-nums text-faint">{total.toLocaleString()} total</span>
      </div>
      <div className="flex gap-2">
        <div className="flex h-40 w-8 flex-col justify-between text-right text-[10px] tabular-nums text-faint">
          <span>{max}</span><span>{Math.round(max / 2)}</span><span>0</span>
        </div>
        <div className="flex-1">
          <div className="relative h-40 border-b border-l border-line">
            <div className="pointer-events-none absolute inset-x-0 top-0 border-t border-dashed border-line/60" />
            <div className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dashed border-line/60" />
            <div className="relative flex h-full items-end gap-px">
              {data.map((d) => {
                const sum = d.values.reduce((a, b) => a + b, 0);
                const title = `${shortDay(d.day)}: ${series.map((s, i) => `${d.values[i]} ${s.name.toLowerCase()}`).join(", ")}`;
                return (
                  <div key={d.day} title={title} className="group flex h-full flex-1 flex-col-reverse hover:bg-hover/60">
                    {d.values.map((v, i) => v > 0 && (
                      <div key={i} className={cn("w-full min-h-px opacity-80 group-hover:opacity-100", series[i]!.color)} style={{ height: `${(v / max) * 100}%` }} />
                    ))}
                    {sum === 0 && <span className="sr-only">{title}</span>}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-faint">
            <span>{first && shortDay(first)}</span><span>{mid && shortDay(mid)}</span><span>{last && shortDay(last)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
